<template>
    <CollapsibleWidget
        class="editable-boolean-widget editable-select-widget"
        :is-opened="isOpen"
        collapsed-height="47"
        @onChange="toggleOpen"
    >
        <template #name>{{ editable.label }}</template>

        <template #controls>
            <EditableRestrictionControl v-if="showRestrictionControl" :editable="editable" />
        </template>

        <div class="editable-widget__body">
            <div v-if="readOnly" class="editable-widget__body--readonly" title="Read Only" @click.stop.prevent></div>
            <div class="deliverable-value-item">
                <LabeledSelect
                    :options="options"
                    :placeholder="groupPlaceholder"
                    :model-value="getEditableValue(editable, groupsAffectingEditable, null)"
                    :label="EditableGroupScopeLabel"
                    @update="onAllChange($event, groupsAffectingEditable)"
                />
            </div>

            <LockableRow
                v-for="(label, templateId) in templateLabels"
                v-show="isOpen"
                :key="getForKey(editable, templateId)"
                :locked="isTemplateOverwrite(editable, groupsAffectingEditable, templateId)"
                @unlock="onUnlock(editable, groupsAffectingEditable, templateId)"
            >
                <LabeledSelect
                    :label="label"
                    :label-color="getLabelColor(editable, groupsAffectingEditable, templateId)"
                    :options="options"
                    :model-value="getEditableValue(editable, groupsAffectingEditable, templateId)"
                    class="list-view__item"
                    @update="onUpdate($event, groupsAffectingEditable, templateId)"
                />
            </LockableRow>
        </div>
    </CollapsibleWidget>
</template>

<script lang="ts">
import { defineComponent, toRef, type PropType } from "vue";
import type { Editable } from "hoxton-types";

import CollapsibleWidget from "@/components/Campaign/widgets/CollapsibleWidget.vue";
import LabeledSelect from "@/components/Campaign/LabeledSelect.vue";
import LockableRow from "@/components/Campaign/LockableRow.vue";
import EditableRestrictionControl from "@/components/Campaign/Editables/EditableRestrictionControl.vue";

import { useToggleEditableItem } from "@/composables/useToggleEditableItem";
import { useEditable } from "@/composables/useEditable";
import { useEditableMethods } from "@/composables/useEditableMethods";
import { useOverwrite } from "@/composables/useOverwrite";

import { EditableGroupScopeLabel } from "@/enums/overwrites";
import * as editableUtils from "@/utils/editables";

export default defineComponent({
    name: "EditableBooleanWidget",
    components: {
        EditableRestrictionControl,
        LockableRow,
        LabeledSelect,
        CollapsibleWidget
    },

    props: {
        editable: {
            type: Object as PropType<Editable>,
            required: true
        },

        groupsAffectingEditable: {
            type: Array as PropType<string[]>,
            required: true
        },

        allExpanded: {
            type: Boolean,
            default: true
        },

        readOnly: {
            type: Boolean,
            default: false
        },

        showRestrictionControl: {
            type: Boolean,
            default: true
        }
    },

    setup(props, { emit }) {
        const allExpanded = toRef(props, "allExpanded");
        const editable = toRef(props, "editable");

        const { isOpen, toggleOpen } = useToggleEditableItem({ allExpanded });
        const { getOverwrite, removeOverwrite, isTemplateOverwrite } = useOverwrite();
        const { getEditableValue, getLabelColor, update, getForKey } = useEditableMethods({ emit });
        const { templateLabels } = useEditable({ editable });

        return {
            isOpen,
            toggleOpen,
            getOverwrite,
            getEditableValue,
            getLabelColor,
            templateLabels,
            update,
            removeOverwrite,
            isTemplateOverwrite,
            getForKey
        };
    },

    data() {
        return {
            options: [
                {
                    label: "true",
                    value: "true"
                },

                {
                    label: "false",
                    value: "false"
                }
            ],
            EditableGroupScopeLabel
        };
    },

    computed: {
        groupPlaceholder() {
            const masterTemplateIds = Object.keys(this.templateLabels);
            const sample = editableUtils.getDefaultValue(this.editable, masterTemplateIds[0]);
            const sameDefaults = !masterTemplateIds.some(masterTemplateId => {
                const val = editableUtils.getDefaultValue(this.editable, masterTemplateId);
                return val !== sample;
            });

            return sameDefaults ? sample : "Select";
        }
    },

    methods: {
        onAllChange(newValue: string, groupsAffectingEditable: string[]) {
            this.onUpdate(newValue, groupsAffectingEditable, null);
        },

        onUnlock(editable: Editable, groupsAffectingEditable: string[], templateId: string) {
            const overwrite = this.getOverwrite(editable, groupsAffectingEditable, templateId);
            this.removeOverwrite(overwrite._id);
        },

        onUpdate(value: string | undefined, groupsAffectingEditable: string[], templateId: string | null) {
            if (value === undefined) {
                return;
            }

            this.update({
                editable: this.editable,
                updateData: { value },
                templateId,
                groupsAffectingEditable
            });
        }
    }
});
</script>
